/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { items } from "../../data/items.ts";
import "../../styles/GunScreensaver.css";

const GunScreensaver = () => {
  const [currentItem, setCurrentItem] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  /* ---------- refs for timers & “session id” ---------- */
  const typingTimeoutRef = useRef(null);
  const autoAdvanceTimeoutRef = useRef(null);
  const sessionRef = useRef(0); // ← NEW: identifies the active round

  const clearAllTimeouts = () => {
    clearTimeout(typingTimeoutRef.current);
    clearTimeout(autoAdvanceTimeoutRef.current);
    typingTimeoutRef.current = null;
    autoAdvanceTimeoutRef.current = null;
  };

  /* ---------- pick a gun + image ---------- */
  const loadNewItem = useCallback(() => {
    clearAllTimeouts();
    sessionRef.current += 1; // bump session → invalidates old timers

    const item = items[Math.floor(Math.random() * items.length)];
    const imgIdx = Math.floor(Math.random() * item.images.length);

    setCurrentItem(item); // triggers typing via useEffect
    setCurrentImageIndex(imgIdx);
  }, []);

  /* ---------- typewriter effect (fires when item changes) ---------- */
  useEffect(() => {
    if (!currentItem) return;

    setDisplayedText("");
    setIsTyping(true);

    let charIndex = 0;
    const mySession = sessionRef.current; // capture session for this run

    // Extract text part from description (remove HTML)
    let cleanDescription = currentItem.description || "";
    if (cleanDescription.includes("<table")) {
        cleanDescription = cleanDescription.split("<table")[0];
    }
    // Remove any other HTML tags if present
    cleanDescription = cleanDescription.replace(/<[^>]*>?/gm, '').trim();

    const typeChar = () => {
      // bail out if a newer session has started
      if (mySession !== sessionRef.current) return;

      if (charIndex <= cleanDescription.length) {
        setDisplayedText(cleanDescription.slice(0, charIndex));
        charIndex += 1;
        typingTimeoutRef.current = setTimeout(typeChar, 50);
      } else {
        setIsTyping(false);
        typingTimeoutRef.current = null;
        autoAdvanceTimeoutRef.current = setTimeout(loadNewItem, 5000); // Wait 5 seconds before next
      }
    };

    typeChar();
    return clearAllTimeouts; // clean up when component unmounts
  }, [currentItem, loadNewItem]);

  /* ---------- auto-scroll while typing ---------- */
  const textContainerRef = useRef(null);
  useEffect(() => {
    textContainerRef.current &&
      (textContainerRef.current.scrollTop =
        textContainerRef.current.scrollHeight);
  }, [displayedText]);

  /* ---------- start on mount ---------- */
  useEffect(() => {
    loadNewItem();
    return clearAllTimeouts;
  }, [loadNewItem]);

  if (!currentItem)
    return <div className="gun-screensaver">Loading…</div>;

  /* ---------- render ---------- */
  return (
    <div className="gun-screensaver">
      <div className="screensaver-content">
        
        {/* Price Circle */}
        <div className="gun-price-circle">
            ${currentItem.price}
        </div>

        {/* Top Section: Title */}
        <div className="gun-top-section">
            <h1 className="gun-title">
                {currentItem.title}
            </h1>
        </div>

        {/* Middle Section: Image */}
        <div className="gun-image-wrapper">
          {currentItem.images && currentItem.images.length > 0 && (
            <img
                src={currentItem.images[currentImageIndex]}
                alt={currentItem.title}
                className="gun-fullscreen-image"
            />
          )}
        </div>

        {/* Tags Section */}
        <div className="gun-tags">
            {(currentItem.category || "").split(',').slice(0, 3).map((tag, index, array) => (
                <React.Fragment key={tag}>
                    <span className="gun-tag">{tag.trim()}</span>
                    {index < array.length - 1 && <span className="gun-tag-separator">|</span>}
                </React.Fragment>
            ))}
        </div>

        {/* Bottom Section: Description */}
        <div className="gun-description-container">
          <div
            ref={textContainerRef}
            className="typewriter-text"
            style={{
              height: "100%",
              width: "100%",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <style>{`.typewriter-text::-webkit-scrollbar{display:none;}`}</style>
            {displayedText}
            {isTyping && <span className="cursor">|</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GunScreensaver;
